import { Fragment } from 'react';
import { Badge, EmptyState, Table, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui';
import type { CampaignReportStep } from '@/lib/types';
import { OPENS_ESTIMATE_REASON } from './ReportStatTiles';

/*
 * Per step: sent, unique opens, unique clicks, bounces, unsubscribes, replies.
 * Under a step with more than one variant, one row per variant (A/B) carrying
 * its subject, and a Winner badge on the variant the server decided — read off
 * `isWinner`, never re-derived from the counts here.
 *
 * ⚠️ The report carries bounces, unsubscribes and replies per STEP only, so a
 * variant row leaves those three cells empty rather than inventing a split.
 */

const TABLE_STYLE = { minWidth: '720px' };

function n(value: number) {
  return value.toLocaleString();
}

export function StepVariantTable({ steps }: { steps: CampaignReportStep[] }) {
  if (steps.length === 0) {
    return <EmptyState title="No steps to report" description="This campaign has no sent steps yet." />;
  }

  return (
    <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
      <Table style={TABLE_STYLE}>
        <caption className="sr-only">Results by step and variant</caption>
        <THead>
          <TR>
            <TH scope="col">Step</TH>
            <TH scope="col" numeric>
              Sent
            </TH>
            <TH scope="col" numeric title={OPENS_ESTIMATE_REASON}>
              Opens (est.)
            </TH>
            <TH scope="col" numeric>
              Clicks
            </TH>
            <TH scope="col" numeric>
              Bounces
            </TH>
            <TH scope="col" numeric>
              Unsubscribes
            </TH>
            <TH scope="col" numeric>
              Replies
            </TH>
          </TR>
        </THead>
        <TBody>
          {steps.map((step) => (
            <Fragment key={step.stepId}>
              <TR>
                <TH scope="row" className="font-medium text-fg">
                  Step {step.stepOrder}
                </TH>
                <TD numeric>{n(step.sent)}</TD>
                <TD numeric>{n(step.uniqueOpens)}</TD>
                <TD numeric>{n(step.uniqueClicks)}</TD>
                <TD numeric>{n(step.bounces)}</TD>
                <TD numeric>{n(step.unsubscribes)}</TD>
                <TD numeric>{n(step.replies)}</TD>
              </TR>
              {step.variants.length > 1 &&
                step.variants.map((variant) => (
                  <TR key={variant.variantId} className="bg-surface-sunken">
                    <TD className="pl-8">
                      <div className="flex min-w-0 items-center gap-2">
                        <Badge tone="neutral" variant="outline">Variant {variant.label}</Badge>
                        {variant.isWinner && (
                          <Badge tone="success" dot>
                            Winner
                          </Badge>
                        )}
                        <span
                          className="min-w-0 max-w-[24rem] truncate text-xs text-fg-secondary"
                          title={variant.subject}
                        >
                          {variant.subject === '' ? 'No subject' : variant.subject}
                        </span>
                      </div>
                    </TD>
                    <TD numeric>{n(variant.sent)}</TD>
                    <TD numeric>{n(variant.uniqueOpens)}</TD>
                    <TD numeric>{n(variant.uniqueClicks)}</TD>
                    <TD numeric aria-hidden="true" />
                    <TD numeric aria-hidden="true" />
                    <TD numeric aria-hidden="true" />
                  </TR>
                ))}
            </Fragment>
          ))}
        </TBody>
      </Table>
    </TableWrap>
  );
}
